import { Link } from 'react-router-dom';
import { useAppDispatch } from '@/hooks';
import { setAgeModalOpen, setRulesModalOpen } from '@/store/slices/uiSlice';
import useContactSupport from '@/hooks/useContactSupport';
import InstallButton from './InstallButton';
import { BRAND } from '@/config/brand';
import BrandLogo from '@/components/common/BrandLogo';

type FooterLink = { label: string; to?: string; onClick?: () => void };

const LinkColumn = ({ title, links }: { title: string; links: FooterLink[] }) => (
  <div>
    <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-shell-fg">{title}</h3>
    <ul className="space-y-2">
      {links.map((link) => (
        <li key={link.label}>
          {link.to ? (
            <Link to={link.to} className="text-sm text-shell-muted transition hover:text-gold">
              {link.label}
            </Link>
          ) : (
            <button
              type="button"
              onClick={link.onClick}
              className="text-sm text-shell-muted transition hover:text-gold"
            >
              {link.label}
            </button>
          )}
        </li>
      ))}
    </ul>
  </div>
);

const SocialButton = ({
  icon,
  label,
  onClick,
  href,
}: {
  icon: string;
  label: string;
  onClick?: () => void;
  href?: string;
}) => {
  const className =
    'flex size-10 items-center justify-center rounded-full border border-shell-border bg-shell-raised transition hover:border-gold';
  const img = <img src={icon} alt="" className="size-5 rounded-full" />;
  return href ? (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className={className}
    >
      {img}
    </a>
  ) : (
    <button type="button" onClick={onClick} aria-label={label} className={className}>
      {img}
    </button>
  );
};

const Footer = () => {
  const dispatch = useAppDispatch();
  const { handleTelegramSupportClick, handleWhatsAppSupportClick } = useContactSupport();

  const helpLinks: FooterLink[] = [
    { label: 'Rules', onClick: () => dispatch(setRulesModalOpen(true)) },
    { label: 'Terms & Conditions', to: '/terms-and-conditions' },
    { label: 'Responsible Gaming', onClick: () => dispatch(setAgeModalOpen(true)) },
    { label: 'Contact Support', onClick: handleTelegramSupportClick },
  ];

  return (
    <footer className="mt-6 border-t border-shell-border bg-shell-panel text-shell-fg">
      <div className="mx-auto grid max-w-[1400px] gap-8 px-5 py-10 sm:grid-cols-[2fr_1fr]">
        {/* Brand */}
        <div className="space-y-4">
          <Link to="/home" className="inline-block">
            <BrandLogo className="h-12 w-auto object-contain" />
          </Link>
          <p className="max-w-xs text-sm leading-relaxed text-shell-muted">
            Sports exchange and live casino with fast withdrawals and 24/7 support.
          </p>
          <div className="flex items-center gap-2">
            <SocialButton
              icon="/icons/telegram.png"
              label="Telegram"
              onClick={handleTelegramSupportClick}
            />
            <SocialButton
              icon="/icons/whatsapp.png"
              label="WhatsApp"
              onClick={handleWhatsAppSupportClick}
            />
            <SocialButton icon="/icons/instagram.png" label="Instagram" href="#" />
          </div>
        </div>

        <LinkColumn title="Help" links={helpLinks} />
      </div>

      {/* Trust strip */}
      <div className="border-t border-shell-border">
        <div className="mx-auto flex max-w-[1400px] flex-col items-center justify-between gap-4 px-5 py-5 md:flex-row">
          <div className="flex items-center gap-3">
            <img src="/images/secure.png" alt="" className="h-8 w-auto" />
            <div>
              <p className="text-sm font-semibold">100% Safe</p>
              <p className="text-xs text-shell-muted">Protected connection and encrypted data.</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <InstallButton />
            <button
              type="button"
              onClick={() => dispatch(setAgeModalOpen(true))}
              aria-label="18+ responsible gaming"
              className="flex size-10 items-center justify-center rounded-full bg-body-inverse p-1.5 transition hover:ring-2 hover:ring-gold"
            >
              <img src="/images/18plus.png" alt="18+" className="size-full object-contain" />
            </button>
            <span className="flex size-10 items-center justify-center rounded-full bg-body-inverse p-1.5">
              <img src="/images/gamecare.png" alt="GamCare" className="size-full object-contain" />
            </span>
            <span className="flex size-10 items-center justify-center rounded-full bg-body-inverse p-1.5">
              <img
                src="/images/gt.png"
                alt="Gambling Therapy"
                className="size-full object-contain"
              />
            </span>
          </div>
        </div>
      </div>

      <div className="border-t border-shell-border px-5 py-4 text-center text-xs text-shell-muted">
        © {new Date().getFullYear()} {BRAND.domain}. All rights reserved. 18+ only — please play
        responsibly.
      </div>
    </footer>
  );
};

export default Footer;
